/**
 * Scores and Workflow API clients.
 */

import { BaseClient } from '../core/client';
import type { RequestOptions } from '../core/config';
import type {
  CustomerScore,
  ScoreBreakdown,
  Workflow,
  WorkflowFilters,
  WorkflowListResponse,
} from './types';

export class ScoresClient extends BaseClient {
  /**
   * Get the current risk score for a customer.
   */
  async getScore(
    customerId: string,
    requestOptions?: RequestOptions
  ): Promise<CustomerScore> {
    return this.requestWithRetry<CustomerScore>(
      `/api/v1/scores/${encodeURIComponent(customerId)}`,
      { method: 'GET' },
      undefined,
      undefined,
      requestOptions
    );
  }

  /**
   * Get a detailed score breakdown for a customer.
   */
  async getScoreBreakdown(
    customerId: string,
    requestOptions?: RequestOptions
  ): Promise<ScoreBreakdown> {
    return this.requestWithRetry<ScoreBreakdown>(
      `/api/v1/scores/${encodeURIComponent(customerId)}/breakdown`,
      { method: 'GET' },
      undefined,
      undefined,
      requestOptions
    );
  }
}

export class WorkflowClient extends BaseClient {
  /**
   * Get the current workflow status for a customer.
   */
  async getWorkflowStatus(
    customerId: string,
    requestOptions?: RequestOptions
  ): Promise<Workflow> {
    return this.requestWithRetry<Workflow>(
      `/api/v1/workflows/${encodeURIComponent(customerId)}/status`,
      { method: 'GET' },
      undefined,
      undefined,
      requestOptions
    );
  }

  /**
   * List workflows with optional filters.
   */
  async listWorkflows(
    filters?: WorkflowFilters,
    requestOptions?: RequestOptions
  ): Promise<WorkflowListResponse> {
    const queryString = this.buildQueryString((filters || {}) as Record<string, unknown>);
    return this.requestWithRetry<WorkflowListResponse>(
      `/api/v1/workflows${queryString}`,
      { method: 'GET' },
      undefined,
      undefined,
      requestOptions
    );
  }

  /**
   * Get a specific workflow by ID.
   */
  async getWorkflow(
    workflowId: string,
    requestOptions?: RequestOptions
  ): Promise<Workflow> {
    return this.requestWithRetry<Workflow>(
      `/api/v1/workflows/${encodeURIComponent(workflowId)}`,
      { method: 'GET' },
      undefined,
      undefined,
      requestOptions
    );
  }
}
